import {
  BioBlend,
  Bunker,
  CIICFShipType,
  FormattedReportRequest,
  ReportBunkerRequest,
  ReportCiiCorrectionRequest,
  ReportFormInterface,
  ReportType,
  ReportWriteFields,
  ShipSettings,
} from 'src/types';
import { compareDate } from './sort.utils';
import { formatDateYYYYMMDD } from './date.utils';
import { getPrimaryDocumentUuid, toDocumentUuidArray } from './document.utils';
import { toNumber } from './number.utils';

export const formatReportForRequest = (
  report: ReportFormInterface,
  imo: number,
  ciiCfShipType: CIICFShipType,
  localReports: ReportFormInterface[] = [],
  localBioBlends: BioBlend[] = [],
  shipSettings?: ShipSettings,
  shouldCollectLngN2Inputs = false,
): FormattedReportRequest => {
  const { bunkers = [], reportDate, localTime, timezone, port, nextCargoPort, previousCargoPort } = report;

  const formattedBunkers = bunkers.map((bunker) => {
    const isLNG =
      bunker.grade === 'lng' && (shouldCollectLngN2Inputs || ciiCfShipType === 'liquefied_gas_tanker');
    let result: ReportBunkerRequest = {
      grade: bunker.grade,
      id: bunker.id ? bunker.id : null,
      remainingOnboardMt: isLNG ? null : toNumber(bunker.remainingOnboardMt),
      consumptionMt: toNumber(bunker.consumptionMt),
      receivedMt: isLNG ? null : toNumber(bunker.receivedMt),
      mainEngineConsumptionMT: null,
      auxConsumptionMT: null,
      boilerConsumptionMT: null,
      inertGasGeneratorConsumptionMT: null,
      gasCombustionUnitConsumptionMT: null,
      ciiCorrectionFactor: generateCiiCfPayload(bunker),
    };
    const hasConsumptionDetails = bunker.consumptionDetails && Object.keys(bunker.consumptionDetails).length !== 0;
    if (hasConsumptionDetails) {
      const hasGasCombustionUnitConsumptionMT = ['lng', 'propane', 'butane'].includes(bunker.grade);
      result = {
        ...result,
        mainEngineConsumptionMT: toNumber(bunker.consumptionDetails?.mainEngineConsumptionMT),
        auxConsumptionMT: toNumber(bunker.consumptionDetails?.auxConsumptionMT),
        boilerConsumptionMT: toNumber(bunker.consumptionDetails?.boilerConsumptionMT),
        inertGasGeneratorConsumptionMT: toNumber(bunker.consumptionDetails?.inertGasGeneratorConsumptionMT ?? ''),
        gasCombustionUnitConsumptionMT: hasGasCombustionUnitConsumptionMT
          ? toNumber(bunker.consumptionDetails?.gasCombustionUnitConsumptionMT)
          : null,
      };
    }

    const hasBlend = !!(bunker.bioBlend && Object.keys(bunker.bioBlend).length);
    if (!hasBlend && bunker.receivedMt && toNumber(bunker.receivedMt) !== 0) {
      const isDebunkering = toNumber(bunker.receivedMt) < 0;
      let timestamp;
      if (bunker.bunkerDeliveryNote?.bdnDate) {
        const dateInUTC = getUtcTime(
          new Date(bunker.bunkerDeliveryNote.bdnDate),
          bunker.bunkerDeliveryNote.bdnTime ? bunker.bunkerDeliveryNote.bdnTime : '00:00',
          bunker.bunkerDeliveryNote.bdnUtcOffset ?? ''
        );
        timestamp = dateInUTC.toISOString();
      }

      const rawDocumentUuid = getPrimaryDocumentUuid(
        bunker.bunkerDeliveryNote?.documentUuid,
        bunker.bunkerDeliveryNote?.documents
      );
      const rawBdnUuid = bunker.bunkerDeliveryNote?.uuid ?? null;
      const hasBdnMetadata = Boolean(
        bunker.bunkerDeliveryNote?.bdnNumber ||
          bunker.bunkerDeliveryNote?.bdnFuelType ||
          bunker.bunkerDeliveryNote?.bdnDate ||
          bunker.bunkerDeliveryNote?.bdnUtcOffset ||
          bunker.bunkerDeliveryNote?.sulphurContent ||
          bunker.bunkerDeliveryNote?.viscosity ||
          bunker.bunkerDeliveryNote?.isoGrade
      );
      const documentUuid =
        rawDocumentUuid ?? (isDebunkering && rawBdnUuid && !hasBdnMetadata ? rawBdnUuid : null);
      const bdnUuid = rawBdnUuid && documentUuid === rawBdnUuid ? null : rawBdnUuid;

      result = {
        ...result,
        bdn: {
          uuid: bdnUuid,
          documents: toDocumentUuidArray(documentUuid),
          bdnNumber: bunker.bunkerDeliveryNote?.bdnNumber ? bunker.bunkerDeliveryNote?.bdnNumber : null,
          bdnFuelType: bunker.bunkerDeliveryNote?.bdnFuelType ? bunker.bunkerDeliveryNote?.bdnFuelType : null,
          bdnDate:
            bunker.bunkerDeliveryNote?.bdnDate &&
            bunker.bunkerDeliveryNote?.bdnTime &&
            bunker.bunkerDeliveryNote?.bdnUtcOffset
              ? (timestamp as string)
              : null,
          bdnUtcOffset: bunker.bunkerDeliveryNote?.bdnUtcOffset ? bunker.bunkerDeliveryNote?.bdnUtcOffset : null,
          sulphurContent: bunker.bunkerDeliveryNote?.sulphurContent
            ? toNumber(bunker.bunkerDeliveryNote.sulphurContent)
            : null,
          viscosity: bunker.bunkerDeliveryNote?.viscosity ? toNumber(bunker.bunkerDeliveryNote.viscosity) : null,
          isoGrade: bunker.bunkerDeliveryNote?.isoGrade ? bunker.bunkerDeliveryNote?.isoGrade : null,
        },
      };
    }

    if (hasBlend) {
      const bioBlend = bunker.bioBlend as BioBlend;
      const bdnDate =
        bioBlend.bdnDate && bioBlend.bdnTime && bioBlend.bdnUtcOffset
          ? getUtcTime(new Date(bioBlend.bdnDate), bioBlend.bdnTime, bioBlend.bdnUtcOffset).toISOString()
          : null;
      const bdnUuid = bunker.bunkerDeliveryNote?.uuid ? bunker.bunkerDeliveryNote?.uuid : bioBlend.bdnUuid;
      result = {
        ...result,
        grade: 'blend',
        bdn: {
          uuid: bdnUuid ? bdnUuid : null,
          bdnNumber: bioBlend.bdnNumber,
          bdnFuelType: bioBlend.blendGrade,
          bdnDate,
          bdnUtcOffset: bioBlend.bdnUtcOffset || null,
          documents: toDocumentUuidArray(bioBlend.bdnDocumentUuid),
          sulphurContent: bioBlend.sulphurContent ? toNumber(bioBlend.sulphurContent) : null,
          viscosity: bioBlend.viscosity ? toNumber(bioBlend.viscosity) : null,
          isoGrade: bioBlend.isoGrade ? bioBlend.isoGrade : null,
        },
        blend: {
          uuid: bioBlend.uuid ? bioBlend.uuid : null,
          grade: bioBlend.blendGrade,
          name: bioBlend.name,
          components: bioBlend.components.map((item) => {
            return {
              uuid: item.uuid,
              componentGrade: item.componentGrade,
              receivedMt: item.receivedMt ? toNumber(item.receivedMt) : null,
              lcvPos: item.lcvPos ? toNumber(item.lcvPos) : null,
              euGhgIntensity: item.euGhgIntensity ? toNumber(item.euGhgIntensity) : null,
              sustainabilityReference: item.sustainabilityReference ? item.sustainabilityReference : null,
              documents: item.posDocumentRemovalId ? [] : toDocumentUuidArray(item.posDocumentUuid),
            };
          }),
        },
      };
      if (toNumber(result.receivedMt) < 0) {
        if (result.bdn) {
          result.bdn.bdnNumber = null;
          result.bdn.bdnFuelType = null;
          result.bdn.bdnDate = null;
          result.bdn.bdnUtcOffset = null;
          result.bdn.sulphurContent = null;
          result.bdn.viscosity = null;
          result.bdn.isoGrade = null;
        }
        if (result.bdn?.uuid && isNewReceivedMt(result.bdn.uuid, report, localReports)) {
          result.bdn.uuid = null;
        }
      }

      if (toNumber(result.receivedMt) === 0 && isBdnSynced(bioBlend, localBioBlends) && isBlendSynced(bioBlend, localBioBlends)) {
        if (result.bdn?.uuid && !isPreviousReceivedMtNegative(result.bdn.uuid, report, localReports)) {
          delete result.bdn;
        } else if (result.bdn?.uuid && isPreviousReceivedMtNegative(result.bdn.uuid, report, localReports)) {
          result.bdn.bdnNumber = null;
          result.bdn.bdnFuelType = null;
          result.bdn.bdnDate = null;
          result.bdn.bdnUtcOffset = null;
          result.bdn.sulphurContent = null;
          result.bdn.viscosity = null;
          result.bdn.isoGrade = null;
        }
      } else if (!isBdnSynced(bioBlend, localBioBlends) || !isBlendSynced(bioBlend, localBioBlends)) {
        if (result.bdn?.uuid) {
          result.bdn.uuid = bioBlend.bdnUuid;
        }
      }
    }
    return result;
  });

  type ReportObjectKey = keyof typeof report;

  const numberProperties = [
    'sailedDistanceNm',
    'speedOverGroundKt',
    'speedThroughWaterKt',
    'latitude',
    'longitude',
    'cargoQuantityMt',
    'cargoQuantityCars',
    'cargoQuantityM3',
    'cargoQuantityTEU',
    'timeSinceLastReport',
    'timeAtAnchor',
    'timeDrifting',
    'opsKWH',
    'opsDurationHRS',
  ];
  numberProperties.forEach((key) => {
    if (key in report && report[key as ReportObjectKey] !== null) {
      report = { ...report, [key]: toNumber(report[key as ReportObjectKey]) };
    }
  });
  const nullableNumberProperties = ['ctmsN2MolPct', 'ctmsLngAverageMolarMassKgPerKmol'];
  nullableNumberProperties.forEach((key) => {
    if (key in report) {
      const value = report[key as ReportObjectKey];
      report = { ...report, [key]: value === null || value === '' ? null : toNumber(value) };
    }
  });
  const reportDatified = new Date(reportDate as Date);
  const month = ('0' + (reportDatified.getMonth() + 1)).slice(-2);
  const day = ('0' + reportDatified.getDate()).slice(-2);
  const reportedTime = `${reportDatified.getFullYear()}-${month}-${day}T${localTime}:00${timezone}`;
  const allowedKeys = allowedKeysMap[report.reportType];
  const nullableKeys = new Set([...nullableNumberProperties, 'ctmsDocumentUuid']);
  const reportKeys = Object.keys(report) as (keyof ReportFormInterface)[];
  const filteredReport = reportKeys
    .filter(
      (key) =>
        key !== 'localId' &&
        report[key] !== 'new' &&
        (report[key] === 0 ||
          report[key] === false ||
          !!report[key] ||
          (report[key] === null && nullableKeys.has(key)))
    )
    .filter((key) => allowedKeys.includes(key))
    .reduce((accum, key) => ({ ...accum, [key]: report[key] }), {});

  const newReport: FormattedReportRequest = {
    ...filteredReport,
    bunkers: formattedBunkers,
    reportedTime,
    utcOffset: timezone,
    source: 'siglar-web-form',
  };

  if (port) {
    newReport.port = port.unlocode;
  }
  if (nextCargoPort) {
    newReport.nextCargoPort = nextCargoPort.unlocode;
  }
  if (previousCargoPort) {
    newReport.previousCargoPort = previousCargoPort.unlocode;
  }
  const hasEDN =
    report.reportType === 'cast_off' &&
    report.location === 'at_berth' &&
    toNumber(report.opsKWH) > 0 &&
    [report.ednNumber, report.ednDate, report.ednTime, report.ednUtcOffset].every(Boolean);
  if (hasEDN) {
    const dateTimeString = `${formatDateYYYYMMDD(report.ednDate as string)}T${report.ednTime}:00${report.ednUtcOffset}`;
    newReport.edns = [
      {
        ednNumber: report.ednNumber as string,
        date: new Date(dateTimeString).toISOString(),
        utcOffset: report.ednUtcOffset as string,
        uuid: report.ednUuid ? report.ednUuid : null,
        documents: toDocumentUuidArray(report.ednDocumentUuid),
      },
    ];
  }

  removeExtraFields(newReport);
  addReportedCiiTypes(newReport, shipSettings);
  addMainEngineType(newReport, imo);
  return newReport;
};

const specialEventKeys = [
  'reportType',
  'reportDate',
  'localTime',
  'timezone',
  'latitude',
  'longitude',
  'modeOfOperation',
  'sailedDistanceNm',
  'speedOverGroundKt',
  'speedThroughWaterKt',
  'timeAtAnchor',
  'timeSinceLastReport',
  'timeDrifting',
  'remark',
];

const commonNormalReportKeys = [
  'bunkers',
  'latitude',
  'localTime',
  'longitude',
  'port',
  'remark',
  'reportDate',
  'reportType',
  'reportedTime',
  'sailedDistanceNm',
  'source',
  'timeSinceLastReport',
  'timezone',
  'utcOffset',
];

const allowedKeysMap: Record<ReportType, string[]> = {
  cast_off: [
    ...commonNormalReportKeys,
    'cargoQuantityMt',
    'cargoQuantityCars',
    'cargoQuantityM3',
    'cargoQuantityTEU',
    'condition',
    'ctmsN2MolPct',
    'ctmsLngAverageMolarMassKgPerKmol',
    'ctmsDocumentUuid',
    'location',
    'nextCargoPort',
    'previousCargoPort',
    'portActivity',
    'timeAtAnchor',
    'timeDrifting',
    'opsKWH',
    'opsDurationHRS',
    'EDN',
  ],
  noon: [
    ...commonNormalReportKeys,
    'location',
    'portActivity',
    'speedOverGroundKt',
    'speedThroughWaterKt',
    'timeAtAnchor',
    'timeDrifting',
    'opsDurationHRS',
  ],
  cosp: [
    ...commonNormalReportKeys,
    'timeAtAnchor',
    'timeDrifting',
  ],
  eosp: [
    ...commonNormalReportKeys,
    'portActivity',
    'speedOverGroundKt',
    'speedThroughWaterKt',
  ],
  all_fast: [
    ...commonNormalReportKeys,
    'portActivity',
    'location',
    'speedOverGroundKt',
    'speedThroughWaterKt',
    'timeAtAnchor',
    'timeDrifting',
    'cargoQuantityMt',
    'cargoQuantityM3',
    'ctmsN2MolPct',
    'ctmsLngAverageMolarMassKgPerKmol',
    'ctmsDocumentUuid',
  ],
  special_event_start: specialEventKeys,
  special_event_end: specialEventKeys,
};

const removeExtraFields = (newReport: ReportWriteFields) => {
  if (newReport.reportType === 'cast_off') {
    if (newReport.condition === 'ballast') {
      const cargoQuantities: (keyof ReportWriteFields)[] = ['cargoQuantityCars', 'cargoQuantityTEU'];
      for (const key of cargoQuantities) {
        if (key in newReport) {
          delete newReport[key];
        }
      }
    }

    const isLocationSts = newReport.location
      ? ['sts_within_port_limits', 'sts_outside_port_limits'].includes(newReport.location)
      : false;
    if (!isLocationSts) {
      delete newReport.timeAtAnchor;
      delete newReport.timeDrifting;
    }
  }

  if (newReport.reportType === 'noon') {
    const location = newReport.location;
    if (location === 'at_berth') {
      delete newReport.speedOverGroundKt;
      delete newReport.speedThroughWaterKt;
      delete newReport.timeDrifting;
      delete newReport.timeAtAnchor;
    } else if (location === 'in_port') {
      delete newReport.speedOverGroundKt;
      delete newReport.speedThroughWaterKt;
    } else if (location === 'at_sea') {
      delete newReport.port;
      delete newReport.portActivity;
      delete newReport.timeAtAnchor;
    } else if (location === 'sts_within_port_limits' || location === 'sts_outside_port_limits') {
      delete newReport.speedOverGroundKt;
      delete newReport.speedThroughWaterKt;
    }
  }
};

export const getUtcTime = (date: Date, time: string, timezone: string): Date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateTimeString = `${year}-${month}-${day}T${time}:00${timezone}`;
  const utcTime = new Date(dateTimeString);
  return utcTime;
};

export const getPreviousReport = (reports: ReportFormInterface[], currentReport: ReportFormInterface) => {
  let currentReportDate = new Date(currentReport.reportDate as Date);
  if (currentReport.localTime && currentReport.timezone) {
    currentReportDate = getUtcTime(currentReportDate, currentReport.localTime, currentReport.timezone);
  }
  return [...reports]
    .map((report) => {
      return {
        ...report,
        reportDate: new Date(report.reportedTime),
      };
    })
    .sort(({ reportDate: a }, { reportDate: b }) => compareDate(a as Date, b as Date, false))
    .find(
      ({ reportDate, id }) =>
        new Date(reportDate as Date).getTime() < new Date(currentReportDate).getTime() && id !== currentReport.id
    );
};

const addReportedCiiTypes = (report: ReportWriteFields, settings?: ShipSettings) => {
  if (settings?.ciiShipType) {
    report.ciiShipTypeAsReported = settings?.ciiShipType;
  }
  if (settings?.ciiDischargePumpType) {
    report.ciiPumpTypeAsReported = settings?.ciiDischargePumpType;
  }
};

const addMainEngineType = (report: ReportWriteFields, imo: number) => {
  const meEnergyConverterAsReported = localStorage.getItem(`${imo}-me-energy-converter-as-reported`);
  if (meEnergyConverterAsReported) {
    report.meEnergyConverterAsReported = meEnergyConverterAsReported;
  }
};

const isNewReceivedMt = (bdnUuid: string, report: ReportFormInterface, localReports: ReportFormInterface[]): boolean => {
  if (report.id === 'new') {
    return true;
  }
  const reports = localReports || [];
  if (reports.length === 0) {
    return true;
  }
  const oldReport = reports.find((item) => item.id === report.id);
  if (!oldReport) {
    return true;
  }
  const bunker = oldReport.bunkers.find((item) => {
    return item.bunkerDeliveryNote?.uuid === bdnUuid;
  });
  if (!bunker) {
    return true;
  }
  if (toNumber(bunker.receivedMt) > 0) {
    return true;
  }
  if (toNumber(bunker.receivedMt) === 0 && bunker.bioBlend?.bdnUuid === bdnUuid) {
    return true;
  }

  return false;
};

const isBlendSynced = (bioBlend: BioBlend, localBioBlends: BioBlend[]): boolean => {
  if (!bioBlend.uuid) {
    return false;
  }
  const syncedBioBlends = localBioBlends || [];
  const syncedBioBlend = syncedBioBlends.find((item) => item.bdnUuid === bioBlend.bdnUuid);
  if (!syncedBioBlend) {
    return false;
  }

  const synced =
    syncedBioBlend.blendGrade === bioBlend.blendGrade &&
    syncedBioBlend.name === bioBlend.name &&
    JSON.stringify(syncedBioBlend.components) === JSON.stringify(bioBlend.components);
  return synced;
};

const isBdnSynced = (bioBlend: BioBlend, localBioBlends: BioBlend[]): boolean => {
  if (!bioBlend.bdnUuid) {
    return false;
  }
  const syncedBioBlends = localBioBlends || [];
  const syncedBioBlend = syncedBioBlends.find((item) => item.bdnUuid === bioBlend.bdnUuid);
  if (!syncedBioBlend) {
    return false;
  }
  const synced =
    syncedBioBlend.bdnDate === bioBlend.bdnDate &&
    syncedBioBlend.bdnTime === bioBlend.bdnTime &&
    syncedBioBlend.bdnUtcOffset === bioBlend.bdnUtcOffset &&
    syncedBioBlend.bdnNumber === bioBlend.bdnNumber &&
    syncedBioBlend.blendGrade === bioBlend.blendGrade &&
    syncedBioBlend.sulphurContent === bioBlend.sulphurContent &&
    syncedBioBlend.viscosity === bioBlend.viscosity &&
    syncedBioBlend.isoGrade === bioBlend.isoGrade;

  return synced;
};

const isPreviousReceivedMtNegative = (bdnUuid: string, report: ReportFormInterface, localReports: ReportFormInterface[]) => {
  if (report.id === 'new') {
    return false;
  }
  const reports = localReports || [];
  if (reports.length === 0) {
    return false;
  }
  const oldReport = reports.find((item) => item.id === report.id);
  if (!oldReport) {
    return false;
  }
  const bunker = oldReport.bunkers.find((item) => {
    return item.bunkerDeliveryNote?.uuid === bdnUuid;
  });
  if (!bunker) {
    return false;
  }
  return toNumber(bunker.receivedMt) < 0;
};

const generateCiiCfPayload = (bunker: Bunker) => {
  const hasBioBlend = bunker.bioBlend && bunker.bioBlend.blendGrade;
  const result: ReportCiiCorrectionRequest = {
    reportBunkerID: bunker.id || null,
    coolingKWH: null,
    coolingSFOCGKWH: null,
    dischargeBoilerMT: null,
    dischargeKWH: null,
    dischargeSFOCGKWH: null,
    dischargeStandaloneMT: null,
    grade: hasBioBlend ? 'blend' : bunker.grade,
    heatingBoilerMT: null,
    id: bunker.ciiCf?.id || null,
  };
  if (bunker.ciiCf) {
    const ciiCf = bunker.ciiCf;
    const keys = Object.keys(bunker.ciiCf);
    keys.forEach((key) => {
      if (key !== 'id' && key !== 'reportBunkerID') {
        // Keep bracket lookup's legacy primitive boxing; this does not validate stored CII data.
        const value: unknown = Reflect.get(Object(ciiCf), key);
        result[key] = toNumber(value);
      }
    });
  }
  return result;
};
